import { useRef, useState, type FormEvent } from 'react'
import { uuid7 } from '../api/uuid'
import { confirmDiscard, useUnsavedChanges } from '../components/unsaved'
import { useStore } from '../store/context'
import { useWorkspaceDraft } from '../workspace/drafts'

/** Today's composer, appending a discussion to this issue's Record; with `parent`, a reply to that comment. */
export function IssueComment({ issueId, parent, onAdded, onClose }: { issueId: string; parent?: string; onAdded: (id: string) => void; onClose?: () => void }) {
  const store = useStore()
  const label = parent ? 'Reply' : 'Leave a comment'
  const [body, setBody] = useWorkspaceDraft(parent ? `issue.${issueId}.reply.${parent}` : `issue.${issueId}.comment`, '', undefined, value => value.trim() !== '')
  const [sending, setSending] = useState(false)
  const submitting = useRef(false)
  const dirty = body.trim() !== ''
  useUnsavedChanges(dirty)

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    const text = body.trim()
    if (!text || submitting.current) return
    submitting.current = true
    setSending(true)
    try {
      const id = uuid7()
      if (await store.addRecord(issueId, 'discussion', text, id, parent)) {
        setBody('')
        onAdded(id)
        onClose?.()
      }
    } finally {
      submitting.current = false
      setSending(false)
    }
  }

  return (
    <form className={parent ? 'composer composer-reply' : 'composer'} onSubmit={(event) => void submit(event)}>
      <textarea
        className="textarea"
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && !event.nativeEvent.isComposing && event.keyCode !== 229) {
            event.preventDefault()
            void submit()
          }
        }}
        placeholder={`${label === 'Reply' ? 'Reply' : 'Leave a comment'} (Markdown; ⌘↵ to send)`}
        aria-label={label}
        rows={3}
        disabled={sending}
      />
      <div className="form-actions">
        {(body !== '' || onClose) && <button type="button" className="button" disabled={sending} onClick={() => { if (!dirty || confirmDiscard()) { setBody(''); onClose?.() } }}>Cancel</button>}
        <button type="submit" className="button button-primary" disabled={sending || !dirty}>{parent ? 'Reply' : 'Comment'}</button>
      </div>
    </form>
  )
}
